/**
 * Post-op `VERIFY` step. Re-reads daily stops and emits a `VerifyReport`
 * with violations (`PHANTOM_STOP_DETECTED`, `PAYLOAD_COUNT_MISMATCH`).
 * Violations flow through `OperationResult.verifyReport` — only a thrown
 * BE error fails the step.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { BackendError } from '../backendError'
import type { StepResult } from '../types'
import type { RouteId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep, RouteOpIntent, VerifyReport } from 'types/modules/routing'

export interface VerifyStepArgs {
  readonly intent: RouteOpIntent
  readonly expectedUnassignedWorkOrderIds?: ReadonlyArray<string>
  readonly expectedAssignedWorkOrderIds?: ReadonlyArray<string>
  readonly expectedFinalCount?: number
}

export type VerifyStepResult = StepResult<'VERIFY', VerifyReport>

const STEP_ID = 'verify'

const fetchDaily = async (
  routeId: RouteId,
  serviceDate: string,
  deps: Deps
): Promise<{ readonly stops: ReadonlyArray<{ workOrderId: string }>; readonly failure?: BackendError }> => {
  try {
    const adapted = await deps.getDailyStops({ routeId, serviceDate }).unwrap()
    const stops = adapted.map(s => ({ workOrderId: String(s.workOrderId) }))
    return { stops }
  } catch (cause: unknown) {
    return { stops: [], failure: extractBackendError(cause) }
  }
}

const beRejectedViolation = (failure: BackendError): VerifyReport['violations'][number] => ({
  kind: 'BE_REJECTED',
  phase: 'VERIFY',
  status: failure.status,
  message: failure.message,
})

const verifyUnassign = async (
  routeId: RouteId,
  serviceDate: string,
  expectedUnassignedIds: ReadonlyArray<string>,
  deps: Deps
): Promise<VerifyReport> => {
  const { stops, failure } = await fetchDaily(routeId, serviceDate, deps)
  const violations: VerifyReport['violations'][number][] = []
  if (failure) violations.push(beRejectedViolation(failure))
  const stopIdsOnRoute = new Set(stops.map(s => s.workOrderId))
  const stillPresent = expectedUnassignedIds.filter(id => stopIdsOnRoute.has(id))
  if (stillPresent.length > 0) {
    violations.push({
      kind: 'PHANTOM_STOP_DETECTED',
      routeId,
      extra: stillPresent.map(id => id as unknown as never) as never,
    })
  }
  return {
    checked: [{ routeId, expectedCount: stops.length, committedCount: stops.length }],
    violations,
  }
}

const verifyAssign = async (
  routeId: RouteId,
  serviceDate: string,
  expectedAssignedIds: ReadonlyArray<string>,
  deps: Deps
): Promise<VerifyReport> => {
  const { stops, failure } = await fetchDaily(routeId, serviceDate, deps)
  const violations: VerifyReport['violations'][number][] = []
  if (failure) violations.push(beRejectedViolation(failure))
  const stopIdsOnRoute = new Set(stops.map(s => s.workOrderId))
  const missing = expectedAssignedIds.filter(id => !stopIdsOnRoute.has(id))
  if (missing.length > 0) {
    violations.push({
      kind: 'PAYLOAD_COUNT_MISMATCH',
      expected: expectedAssignedIds.length,
      actual: expectedAssignedIds.length - missing.length,
      missing: missing.map(id => id as unknown as never) as never,
      extra: [],
    })
  }
  return {
    checked: [{ routeId, expectedCount: stops.length, committedCount: stops.length }],
    violations,
  }
}

const verifyResequenceCount = async (routeId: RouteId, serviceDate: string, expectedCount: number, deps: Deps): Promise<VerifyReport> => {
  const { stops, failure } = await fetchDaily(routeId, serviceDate, deps)
  const violations: VerifyReport['violations'][number][] = []
  if (failure) violations.push(beRejectedViolation(failure))
  if (stops.length !== expectedCount) {
    violations.push({
      kind: 'PAYLOAD_COUNT_MISMATCH',
      expected: expectedCount,
      actual: stops.length,
      missing: [],
      extra: [],
    })
  }
  return {
    checked: [{ routeId, expectedCount, committedCount: stops.length }],
    violations,
  }
}

export const makeVerifyStep = (args: VerifyStepArgs, deps: Deps): PipelineStep<'VERIFY', VerifyStepResult> => {
  return {
    id: STEP_ID,
    phase: 'VERIFY',
    label: 'verify route state',
    applyOrder: 9000,
    run: async () => {
      let report: VerifyReport
      switch (args.intent.kind) {
        case 'unassign': {
          const expected = args.expectedUnassignedWorkOrderIds ?? []
          report = await verifyUnassign(args.intent.routeId, args.intent.serviceDate, expected, deps)
          break
        }
        case 'assign': {
          const expected = args.expectedAssignedWorkOrderIds ?? []
          report = await verifyAssign(args.intent.routeId, args.intent.serviceDate, expected, deps)
          break
        }
        case 'resequence': {
          if (typeof args.expectedFinalCount === 'number') {
            report = await verifyResequenceCount(args.intent.routeId, args.intent.serviceDate, args.expectedFinalCount, deps)
          } else {
            report = { checked: [], violations: [] }
          }
          break
        }
        default:
          report = { checked: [], violations: [] }
      }
      // Verify is a *report*, not a gate — violations land on the report so
      // the caller (toast / dev triage) can render them. Hard-fail only on a
      // BE error (network / 5xx), which is already in `report.violations` as
      // a `BE_REJECTED` kind. Other violations (`PHANTOM_STOP_DETECTED`,
      // `PAYLOAD_COUNT_MISMATCH`) flow through `OperationResult.verifyReport`.
      const beRejected = report.violations.find(v => v.kind === 'BE_REJECTED')
      if (beRejected) return err(beRejected)
      return ok({ phase: 'VERIFY', stepId: STEP_ID, value: report })
    },
  }
}
